import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import {
  listAssessments,
  deleteAssessment,
  clearAllAssessments,
  AssessmentHistoryEntry,
  buildPatientSummary,
} from "../utils/assessmentHistory";

const HistoryPage: React.FC = () => {
  const [assessments, setAssessments] = useState<AssessmentHistoryEntry[]>([]);
  const [confirmClearAll, setConfirmClearAll] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setAssessments(listAssessments());
  }, []);

  const handleLoad = (entry: AssessmentHistoryEntry) => {
    const params = new URLSearchParams(entry.formData);
    navigate(`/assessment?${params.toString()}`);
  };

  const handleDelete = (id: string) => {
    deleteAssessment(id);
    setAssessments(listAssessments());
  };

  const handleClearAll = () => {
    clearAllAssessments();
    setAssessments([]);
    setConfirmClearAll(false);
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <>
      <Helmet>
        <title>Assessment History - Boobalyz</title>
        <meta
          name="description"
          content="View previous breast cancer risk assessments"
        />
      </Helmet>
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                Assessment History
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                {assessments.length > 0
                  ? `${assessments.length} saved assessment${assessments.length !== 1 ? "s" : ""}`
                  : "View your previous breast cancer risk assessments"}
              </p>
            </div>
            <div className="flex gap-3">
              {assessments.length > 0 && !confirmClearAll && (
                <button
                  onClick={() => setConfirmClearAll(true)}
                  className="px-4 py-2 border border-slate-200 bg-white text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Clear All
                </button>
              )}
              {confirmClearAll && (
                <>
                  <button
                    onClick={() => setConfirmClearAll(false)}
                    className="px-4 py-2 border border-slate-200 bg-white text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleClearAll}
                    className="px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Confirm Clear All
                  </button>
                </>
              )}
              <Link
                to="/assessment"
                className="inline-flex items-center px-5 py-2 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors"
              >
                New Assessment
              </Link>
            </div>
          </div>

          {/* Empty state */}
          {assessments.length === 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">
                No Assessments Yet
              </h2>
              <p className="text-slate-500 text-sm mb-6">
                Complete and save an assessment to see your history here.
              </p>
              <Link
                to="/assessment"
                className="inline-flex items-center px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors"
              >
                Start Assessment
              </Link>
            </div>
          )}

          {/* Assessment list */}
          {assessments.length > 0 && (
            <div className="space-y-3">
              {assessments.map((entry) => (
                <div
                  key={entry.id}
                  className="bg-white rounded-xl border border-slate-200 p-5 flex items-center justify-between gap-4"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center shrink-0">
                      <svg
                        className="w-5 h-5 text-blue-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 truncate">
                        {buildPatientSummary(entry.formData)}
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        Saved {formatDate(entry.savedAt)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => handleLoad(entry)}
                      className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Load
                    </button>
                    <button
                      onClick={() => handleDelete(entry.id)}
                      className="px-3 py-2 border border-slate-200 text-slate-500 text-sm rounded-lg hover:bg-slate-50 hover:text-red-600 transition-colors"
                      title="Delete"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default HistoryPage;
